import api from './api'
import { ErrorNotification, SuccessNotification } from './Notification'
import { isManagedUserAccount } from './accessControl'
import { UserRoleType } from '../types/activity_user'

export const PLATFORM_DEFAULT_SENDER_ID = 'Solidaridad'

export const RESERVED_SENDER_IDS = [PLATFORM_DEFAULT_SENDER_ID, 'Solidaridad']

export const SMS_SENDER_ID_MAX_LENGTH = 11

export const SMS_SENDER_ID_PATTERN = /^[a-zA-Z0-9 ]+$/

/** Strip unsupported characters from SMS nickname input */
export const sanitizeSmsSenderIdInput = (value) =>
  String(value || '')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .slice(0, SMS_SENDER_ID_MAX_LENGTH)

export const isReservedSenderId = (senderId) => {
  const normalized = (senderId || '').trim().toLowerCase()
  return RESERVED_SENDER_IDS.includes(normalized)
}

/** Only primary account owners (service providers / processors) may be prompted for a sender ID */
export const isAccountOwnerEligibleForSenderIdPrompt = (user) => {
  if (!user) return false
  if (isManagedUserAccount(user)) return false
  if (user.is_activity_user || user.user_type === 'activity_user' || user.activity_role) return false

  const isServiceProvider = user.user_type === UserRoleType.service_provider
  const isProcessor =
    user.user_type === UserRoleType.processor || user.user_type === UserRoleType.miller

  return isServiceProvider || isProcessor
}

export const hasExistingSenderIdRecord = (senderRecords = []) => {
  const list = Array.isArray(senderRecords) ? senderRecords : []
  return list.some((record) => record.status === 'approved' || record.status === 'pending')
}

export const shouldPromptForSmsSenderId = (
  user,
  { hasSenderIdRecord = false, isDismissed = false } = {}
) => {
  if (!isAccountOwnerEligibleForSenderIdPrompt(user)) return false
  if (hasSenderIdRecord) return false
  if (isDismissed) return false
  return true
}

export const fetchSenderIdPromptState = async (user) => {
  if (!isAccountOwnerEligibleForSenderIdPrompt(user)) {
    return { shouldShow: false, hasSenderIdRecord: false }
  }

  try {
    const smsResponse = await api.get('sms-sender-ids/')
    const senderIds = Array.isArray(smsResponse.data) ? smsResponse.data : []
    const hasSenderIdRecord = hasExistingSenderIdRecord(senderIds)
    const isDismissed = sessionStorage.getItem('sms_modal_dismissed') === 'true'

    return {
      shouldShow: shouldPromptForSmsSenderId(user, { hasSenderIdRecord, isDismissed }),
      hasSenderIdRecord,
    }
  } catch (error) {
    console.error('Failed to fetch SMS Nicknames:', error)
    const isDismissed = sessionStorage.getItem('sms_modal_dismissed') === 'true'
    return {
      shouldShow: shouldPromptForSmsSenderId(user, { hasSenderIdRecord: false, isDismissed }),
      hasSenderIdRecord: false,
    }
  }
}

export const getReservedSenderIdMessage = () =>
  `${RESERVED_SENDER_IDS.join(' and ')} are reserved and cannot be used as a custom SMS Nickname.`

/** Custom ID the user requested (pending or approved) */
export const getSubmittedSenderId = (sender) => sender?.sender_id || ''

/** ID currently used for campaigns (may be account default when custom is disabled) */
export const getActiveSenderId = (sender) =>
  sender?.effective_sender_id || sender?.default_sender_id || sender?.sender_id || ''

/** Approved custom ID label — always sender_id, not effective_sender_id */
export const getApprovedCustomSenderId = (sender) => getSubmittedSenderId(sender)

/** Pending custom request: sender_id differs from the account default */
export const isPendingCustomRequest = (sender) => {
  if (!sender || sender.status === 'approved') return false
  const submitted = (sender.sender_id || '').toUpperCase().trim()
  const accountDefault = (sender.default_sender_id || '').toUpperCase().trim()
  return Boolean(submitted && accountDefault && submitted !== accountDefault)
}

export const isApprovedCustomSenderId = (sender) => {
  if (!sender || sender.status !== 'approved') return false
  const submitted = (sender.sender_id || '').toUpperCase().trim()
  const accountDefault = (sender.default_sender_id || '').toUpperCase().trim()
  return Boolean(submitted && accountDefault && submitted !== accountDefault)
}

/** Custom sender ID on the account (approved first, then pending) */
export const getAccountCustomSenderId = (senders) => {
  const list = Array.isArray(senders) ? senders : []
  const approved = list.find(isApprovedCustomSenderId)
  if (approved) return getApprovedCustomSenderId(approved)
  const pending = list.find(isPendingCustomRequest)
  if (pending) return getSubmittedSenderId(pending)
  return ''
}

/** Label for an approved sender record — prefer sender_id over effective_sender_id */
export const getSenderIdLabel = (record) =>
  getApprovedCustomSenderId(record) || getSubmittedSenderId(record) || getActiveSenderId(record)

/** Sender ID to use when sending SMS (harvest requests, campaigns, etc.) */
export const getOutboundSmsSenderId = (senderRecords = [], selectedLabel = '') => {
  const normalizedSelection = (selectedLabel || '').toUpperCase().trim()
  if (normalizedSelection) return normalizedSelection

  const list = Array.isArray(senderRecords) ? senderRecords : []
  const approved = list.filter((s) => s.status === 'approved')
  const enabled = approved.filter((s) => s.is_enabled)
  const record = enabled[0] || approved[0]
  if (record) {
    const id = getSenderIdLabel(record)
    if (id) return id.toUpperCase().trim()
  }
  return PLATFORM_DEFAULT_SENDER_ID
}

export const buildDefaultSenderId = (user) => {
  if (user?.firstname) {
    return `${PLATFORM_DEFAULT_SENDER_ID}`
  }
  return `${PLATFORM_DEFAULT_SENDER_ID}`
}

const isSenderIdResponse = (data) =>
  Boolean(data?.id || data?.sender_id || data?.effective_sender_id)

const formatSenderIdError = (errorData) => {
  if (!errorData) return 'Failed to save SMS Nickname'
  if (typeof errorData === 'string') return errorData
  if (errorData.detail) return errorData.detail
  if (errorData.message) return errorData.message
  return 'Failed to save SMS Nickname'
}

const patchSenderId = async (id, payload, { user, setUser } = {}) => {
  const { data } = await api.patch(`sms-sender-ids/${id}/`, payload)

  if (isSenderIdResponse(data)) {
    if (user && setUser) {
      const updatedUser = { ...user, ...data }
      setUser(updatedUser)
      window.localStorage.setItem('user', JSON.stringify(updatedUser))
    }
    return { ok: true, data }
  }

  return { ok: false }
}

export const createOrUpdateSenderId = async (
  senderId,
  { user, setUser, existingId } = {}
) => {
  if (isReservedSenderId(senderId)) {
    ErrorNotification(getReservedSenderIdMessage())
    return { ok: false }
  }

  const payload = { sender_id: senderId }

  if (existingId) {
    try {
      const result = await patchSenderId(existingId, payload, { user, setUser })
      if (result.ok) {
        SuccessNotification('SMS Nickname update submitted successfully!')
        return result
      }
      ErrorNotification('Failed to update SMS Nickname')
      return { ok: false }
    } catch (error) {
      ErrorNotification(formatSenderIdError(error?.response?.data))
      return { ok: false }
    }
  }

  try {
    const response = await api.post('sms-sender-ids/', payload)
    const responseData = response.data

    if (isSenderIdResponse(responseData)) {
      if (user && setUser) {
        const updatedUser = { ...user, ...responseData }
        setUser(updatedUser)
        window.localStorage.setItem('user', JSON.stringify(updatedUser))
      }
      SuccessNotification('SMS Nickname request submitted successfully!')
      return { ok: true, data: responseData }
    }

    ErrorNotification('Failed to create SMS Nickname')
    return { ok: false }
  } catch (error) {
    const errorData = error?.response?.data
    const fallbackId = errorData?.existing_sender_id

    if (fallbackId) {
      try {
        const result = await patchSenderId(fallbackId, payload, { user, setUser })
        if (result.ok) {
          SuccessNotification('SMS Nickname updated successfully!')
          return result
        }
        ErrorNotification('Failed to update SMS Nickname')
        return { ok: false }
      } catch (updateError) {
        ErrorNotification(formatSenderIdError(updateError?.response?.data))
        return { ok: false }
      }
    }

    ErrorNotification(formatSenderIdError(errorData))
    return { ok: false }
  }
}
